import React, { useCallback } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import {
  SimpleSingleDropdown,
  SimpleMultiDropdown,
} from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { Label, SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import { withLocalePrefix } from 'shared/utils/translation';
import { useQueryClient } from '@tanstack/react-query';
import { SCORECARD_ATTRIBUTE_MATCH } from 'talent_search/shared/types';
import { SecondaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import RefreshIcon from '@grnhse/seedling/lib/birch/icons/refresh';

type Props = {
  fetchScorecardAttributes: (term: string) => Promise<Item[]>;
  focusAttributes: Item[];
};

const t = withLocalePrefix(
  'talent_matching.rediscovery.filters.scorecard_attribute_filters'
);

const MATCH_TYPE_OPTIONS: Item[] = [
  { label: t('options.any'), value: SCORECARD_ATTRIBUTE_MATCH.ANY },
  { label: t('options.all'), value: SCORECARD_ATTRIBUTE_MATCH.ALL },
];

const ScorecardAttributesSection = ({
  fetchScorecardAttributes,
  focusAttributes,
}: Props) => {
  const queryClient = useQueryClient();
  const { watch, setValue } = useFormContext();
  const matchType: string = watch('scorecard_attribute_match');
  const selectedAttributes: Item[] = watch('scorecard_attribute_filters') ?? [];
  const selectedMatchType = MATCH_TYPE_OPTIONS.find(
    (option) => option.value === matchType
  );

  const fetchAttributes = useCallback(
    (term: string) =>
      queryClient.fetchQuery({
        queryKey: ['scorecardAttributes', term],
        queryFn: async () => {
          const items = await fetchScorecardAttributes(term);
          return { items };
        },
      }),
    [fetchScorecardAttributes, queryClient]
  );

  const setHiringPlanFocusAttributes = useCallback(() => {
    setValue('scorecard_attribute_filters', focusAttributes, {
      shouldDirty: true,
    });
  }, [focusAttributes, setValue]);

  return (
    <Stack spacing={1} alignItems="flex-start">
      <Label>{t('header')}</Label>
      <Stack spacing={2}>
        <SecondaryBody>{t('description')}</SecondaryBody>
        <SimpleSingleDropdown
          ariaLabel={t('match_type_label')}
          items={MATCH_TYPE_OPTIONS}
          selectedItem={selectedMatchType}
          onSelectedItemChange={(val) =>
            setValue('scorecard_attribute_match', val.value, {
              shouldDirty: true,
            })
          }
          clearable={false}
        />
      </Stack>
      <Stack sx={{ width: '100%' }}>
        <SimpleMultiDropdown
          ariaLabel={t('attributes_label')}
          placeholder={t('attributes_label')}
          fetchItems={fetchAttributes}
          selectedItems={selectedAttributes}
          onSelectedItemsChange={(items) =>
            setValue('scorecard_attribute_filters', items, {
              shouldDirty: true,
            })
          }
          preventLoadOnMount={true}
        />
      </Stack>
      <SecondaryButton
        onClick={setHiringPlanFocusAttributes}
        icon={RefreshIcon}
        iconPosition="left"
        size="sm"
      >
        {t('reload_button')}
      </SecondaryButton>
    </Stack>
  );
};

export default ScorecardAttributesSection;
